import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SearchForm from './components/SearchForm';
import SearchResults from './components/SearchResults';
import Login from './components/Login';
import Signup from './components/Signup';
import Profile from './components/Profile';
import UpgradeToPremium from './components/UpgradeToPremium';
import AdminPortal from './components/AdminPortal';
import Alerts from './components/Alerts';
import ForgotPassword from './components/ForgotPassword';
import VerifyEmail from './components/VerifyEmail';
import './App.css';
import ReactGA from 'react-ga4';

ReactGA.initialize('G-4JG7GEQSLZ');

// Debug logging for Google Analytics
console.log('ReactGA initialized');
console.log('window.gtag available:', typeof window.gtag !== 'undefined');
console.log('window.dataLayer available:', typeof window.dataLayer !== 'undefined');

// Check if Google Analytics is loaded
if (typeof window.gtag !== 'undefined') {
  console.log('Google Analytics is loaded!');
} else {
  console.log('Google Analytics is NOT loaded!');
}

function RequireVerifiedEmail({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const allowedPaths = ['/verify-email', '/login', '/signup', '/forgot-password'];

  if (loading) return null;

  // Check if user is on verify-email page and has just completed verification
  const isOnVerifyEmailPage = location.pathname === '/verify-email';
  const hasJustVerified = location.state?.justVerified;

  console.log('🔍 RequireVerifiedEmail check:', {
    currentUserUid: currentUser?.uid,
    currentUserEmail: currentUser?.email,
    customEmailVerified: currentUser?.customEmailVerified,
    currentPath: location.pathname,
    isOnVerifyEmailPage,
    hasJustVerified,
    shouldRedirect: currentUser && !currentUser.customEmailVerified && !allowedPaths.includes(location.pathname) && !(isOnVerifyEmailPage && hasJustVerified)
  });

  // If user is verified and on verify-email page, redirect to home
  if (currentUser && currentUser.customEmailVerified && isOnVerifyEmailPage) {
    console.log('🔄 User is verified, redirecting from /verify-email to home');
    return <Navigate to="/" replace />;
  }

  if (
    currentUser &&
    !currentUser.customEmailVerified && // Use custom verification instead of emailVerified
    !allowedPaths.includes(location.pathname) &&
    !(isOnVerifyEmailPage && hasJustVerified) // Don't redirect if user just verified
  ) {
    console.log('🔄 Redirecting to /verify-email');
    return <Navigate to="/verify-email" replace />;
  }

  return children;
}

function usePageTracking() {
  const location = useLocation();
  React.useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);
}

function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <Router>
      <PageTracker />
      <AuthProvider>
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-grow">
            <RequireVerifiedEmail>
              <Routes>
                <Route path="/" element={<SearchForm />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/results" element={<SearchResults />} />
                <Route path="/upgrade" element={<UpgradeToPremium />} />
                <Route path="/admin" element={<AdminPortal />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/payment-success" element={<Profile />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/__/auth/action" element={<VerifyEmail />} />
              </Routes>
            </RequireVerifiedEmail>
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App; 